import {Injectable} from '@angular/core';
import {ErrorNotificationService} from './error-notification.service';
import {select, Store} from '@ngrx/store';
import {StoreState} from '@redux/reducers';
import {catchError, distinctUntilChanged, map, take} from 'rxjs/operators';
import {Profile} from '@model/profile';
import {HttpErrorResponse} from '@angular/common/http';
import {Observable, of} from 'rxjs';
import {CustomError} from './custom-error';
import {BUSINESS_PROBLEM_TRANSLATION, Problem} from './problem';
import * as Sentry from '@sentry/angular';
import {SentryErrorHandler} from '@sentry/angular';
import {AppConfigurationService} from '../app-configuration.service';
import {environment} from 'src/environments/environment';
import {isPWAInstalled} from '@shared/util/pwa-util';

@Injectable({
  providedIn: 'root'
})
export class ErrorFacadeService extends SentryErrorHandler {

  private _config: any;

  constructor(private _errorNotificationService: ErrorNotificationService,
              private _store: Store<StoreState>,
              private _appConfig: AppConfigurationService) {
    super();
    this._config = this._appConfig.getConfig();
  }

  sendErrorToSentry(error: Error): Observable<string> {
    return this._store.pipe(
      select(state => state.profile),
      take(1),
      map(profile => !!profile ? profile.selectedProfile : {}),
      map(profile => this._buildErrorWithContextData(profile, error)),
      distinctUntilChanged(),
      map(customError => this._buildContextAndSendToSentry(customError, error)),
      catchError(customError => {
        console.error(`error while sending the error to backend : ${customError}`);
        return of(customError);
      })
    );
  }

  notifyTechnicalError(errorId: string) {
    this._errorNotificationService.notifyTechnicalError(errorId);
  }

  notifyBusinessProblem(problem: Problem) {
    this._errorNotificationService.notify(BUSINESS_PROBLEM_TRANSLATION[problem.code]);
  }

  private _buildErrorWithContextData(profile: Profile, error: Error): CustomError {
    let detail;
    if (error instanceof HttpErrorResponse) {
      detail = error.error;
    } else {
      detail = error.stack;
    }
    return {
      name: error.name || null,
      message: error.message || error.toString(),
      profile: profile,
      path: window.location.pathname,
      status: error instanceof HttpErrorResponse ? error.status : null,
      stacktrace: detail ? detail : {}
    };
  }

  isKnownBusinessError(err: any) {
    return err.headers && err.headers.get('Content-Type') &&
      err.headers.get('Content-Type').indexOf('application/problem+json') > -1 &&
      BUSINESS_PROBLEM_TRANSLATION[err.error.code];
  }

  private _buildContextAndSendToSentry(customError: CustomError, error: any): string {
    if (this._config.sentry && this._config.sentry.dsn) {
      Sentry.setContext("App information", {
        version: environment.gitTag || environment.gitCommitId,
        PWA_installed: isPWAInstalled()
      });
      if (customError.profile) {
        Sentry.withScope(scope => {
          scope.setUser({
            id: `${customError.profile.id}`,
            username: `${customError.profile.lastName} ${customError.profile.firstName}`,
          });
          Sentry.setTag('school', `${customError.profile.schoolId}`);
          Sentry.setContext('Profile information', {...customError.profile});
        });
      }
      if (error instanceof HttpErrorResponse) {
        Sentry.setContext('Error information', {...error});
        Sentry.setContext('Error header information', this.extractHeaders(error));
        Sentry.withScope(scope => {
          scope.setFingerprint([this.removeSchoolIdEndpoint(error.url, customError?.profile?.schoolId), String(error.status)]);
        });
        super.handleError(error);
        return Sentry.lastEventId();
      } else {
        super.handleError(error.originalError || error);
        return Sentry.lastEventId();
      }
    }
    return '-1';
  }

  private removeSchoolIdEndpoint(url: string, schoolId: string): string {
    if (schoolId) {
      return url.replace(`/${schoolId}`, '/schoolId');
    }
    return url;
  }

  private extractHeaders(error: HttpErrorResponse) {
    let headers = {};
    if (error.headers) {
      error.headers.keys().forEach(key => headers[key] = error.headers.get(key));
    }
    return headers;
  }
}
